// components/Email/EmailSetup.jsx
import React, { useState, useEffect } from 'react';
import { emailSetupService } from '../../services/emailService';
import { companyService } from '../../services/companyService';
import { EnvelopeIcon, CheckCircleIcon, CogIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';
import './EmailSetup.css';

const EmailSetup = () => {
  const [formData, setFormData] = useState({
    smtpServer: '',
    smtpPort: 587,
    enableSsl: true,
    useTls: true,
    username: '',
    password: '',
    fromEmail: '',
    fromName: ''
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [existingSetup, setExistingSetup] = useState(null);

  // ADDED: this page previously had no concept of "which company" at all --
  // it always loaded/saved a single global row (backend's GetEmailSetup just
  // did `_context.EmailSetup.FirstOrDefaultAsync()`, no company filter), so
  // there was literally no way through this UI to configure SMTP for any
  // company other than whichever one happened to be first in the database.
  // That's exactly why sending invitations for a different company (e.g.
  // company 2) failed with "No active email setup found for company 2" --
  // there was no way to create one for it. Scoping the form by company fixes
  // that, using the backend's existing GET /EmailSetup/all (already returns
  // every company's setup) and the save endpoint's existing CompanyId field
  // (already supported server-side, just never sent by this form).
  const [companies, setCompanies] = useState([]);
  const [allSetups, setAllSetups] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  useEffect(() => {
    loadCompaniesAndSetups();
  }, []);

  // Re-populate the form whenever the selected company (or the fetched list
  // of setups) changes, instead of always showing whatever the single
  // unscoped GET /EmailSetup happened to return.
  useEffect(() => {
    applySetupForSelectedCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, allSetups]);

  const loadCompaniesAndSetups = async () => {
    setLoadingCompanies(true);
    try {
      const [companyList, setupsResult] = await Promise.all([
        companyService.getCompanies(),
        emailSetupService.getAllEmailSetups().catch(() => ({ setups: [] }))
      ]);

      setCompanies(companyList || []);
      setAllSetups(setupsResult?.setups || []);

      // Default to the first company so the form has an unambiguous target
      // rather than silently editing whichever row the backend used to pick.
      setSelectedCompanyId(prev => {
        if (prev && (companyList || []).some(c => String(c.id) === String(prev))) {
          return prev;
        }
        return companyList && companyList.length > 0 ? String(companyList[0].id) : '';
      });
    } catch (error) {
      console.error('❌ Error loading companies/email setups:', error);
      showMessage('Failed to load companies', 'error');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const applySetupForSelectedCompany = () => {
    if (!selectedCompanyId) return;

    const setup = allSetups.find(s => String(s.companyId) === String(selectedCompanyId));

    if (setup) {
      setExistingSetup(setup);
      setFormData({
        smtpServer: setup.smtpServer || '',
        smtpPort: setup.smtpPort || 587,
        enableSsl: setup.enableSsl !== undefined ? setup.enableSsl : true,
        useTls: setup.useTls !== undefined ? setup.useTls : true,
        username: setup.username || '',
        password: '', // Never pre-fill password
        fromEmail: setup.fromEmail || '',
        fromName: setup.fromName || ''
      });
      showMessage(`Loaded email setup for ${setup.companyName || 'this company'}`, 'success');
    } else {
      setExistingSetup(null);
      setFormData({
        smtpServer: '',
        smtpPort: 587,
        enableSsl: true,
        useTls: true,
        username: '',
        password: '',
        fromEmail: '',
        fromName: ''
      });
      showMessage('No email setup configured yet for this company.', 'info');
    }
  };

  const handleCompanyChange = (e) => {
    setSelectedCompanyId(e.target.value);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              (type === 'number' ? parseInt(value) : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCompanyId) {
      showMessage('Please select a company first', 'error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      console.log('📤 Submitting email setup:', formData);
      
      // Validate required fields
      const requiredFields = ['smtpServer', 'smtpPort', 'username', 'fromEmail', 'fromName'];
      const missingFields = requiredFields.filter(field => !formData[field]);
      
      if (missingFields.length > 0) {
        showMessage(`Please fill in all required fields: ${missingFields.join(', ')}`, 'error');
        setLoading(false);
        return;
      }

      // companyId is required so the backend saves/updates THIS company's
      // row (CreateOrUpdateEmailSetup already supports it) instead of
      // falling back to "the first company in the system" when it's missing.
      const result = await emailSetupService.saveEmailSetup({
        ...formData,
        companyId: parseInt(selectedCompanyId, 10)
      });
      console.log('✅ Save response:', result);
      showMessage(result.message || 'Email setup saved successfully!', 'success');
      loadCompaniesAndSetups(); // Reload so the dropdown's "(configured)" tags and this form reflect the save
    } catch (error) {
      console.error('❌ Save error:', error);
      showMessage(error.message || 'Failed to save email setup', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage('');

    try {
      console.log('🧪 Testing email connection:', formData);
      const result = await emailSetupService.testEmailSetup(formData);
      showMessage(result.message || 'Connection test completed!', result.success ? 'success' : 'error');
    } catch (error) {
      console.error('❌ Test error:', error);
      showMessage(error.message || 'Failed to test email connection', 'error');
    } finally {
      setTesting(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const commonProviders = [
    {
      name: 'Gmail',
      server: 'smtp.gmail.com',
      port: 587,
      ssl: true,
      tls: true,
      note: 'Requires App Password for 2FA accounts'
    },
    {
      name: 'Outlook',
      server: 'smtp-mail.outlook.com',
      port: 587,
      ssl: false,
      tls: true,
      note: 'STARTTLS enabled'
    },
    {
      name: 'Office 365',
      server: 'smtp.office365.com',
      port: 587,
      ssl: false,
      tls: true,
      note: 'STARTTLS enabled'
    },
    {
      name: 'Yahoo',
      server: 'smtp.mail.yahoo.com',
      port: 587,
      ssl: true,
      tls: true,
      note: 'Requires App Password'
    }
  ];

  // Validation function
  const validateEmailSetup = (data) => {
    const errors = [];
    
    if (!data.smtpServer) errors.push('SMTP Server is required');
    if (!data.smtpPort || data.smtpPort < 1 || data.smtpPort > 65535) errors.push('Valid SMTP Port is required');
    if (!data.username) errors.push('Username is required');
    if (!data.fromEmail) errors.push('From Email is required');
    if (!data.fromName) errors.push('From Name is required');
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (data.fromEmail && !emailRegex.test(data.fromEmail)) {
      errors.push('Valid From Email is required');
    }
    
    return errors;
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-6 rounded-2xl shadow-md">
      {/* Left Panel */}
      <div className="w-full lg:w-1/4 bg-gradient-to-br from-purple-100 via-white to-indigo-100 rounded-2xl shadow-md p-6 border border-gray-100 h-fit">
        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4">
            <EnvelopeIcon className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Email Setup</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Configure your SMTP settings to send survey invitations and notifications securely.
          </p>
        </div>

        {/* Quick Settings */}
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Settings</h3>
          <div className="space-y-2">
            {commonProviders.map((provider) => (
              <button
                key={provider.name}
                type="button"
                onClick={() => setFormData({
                  ...formData,
                  smtpServer: provider.server,
                  smtpPort: provider.port,
                  enableSsl: provider.ssl,
                  useTls: provider.tls
                })}
                className="w-full text-left p-3 text-xs bg-white border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
              >
                <div className="font-medium text-gray-800">{provider.name}</div>
                <div className="text-gray-500 mt-1">{provider.server}:{provider.port}</div>
                <div className="text-gray-400 text-xs mt-1">{provider.note}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex-1 bg-gradient-to-br from-purple-50 via-white to-indigo-50 rounded-xl shadow-sm p-6 border border-purple-100"
      >
        {message && (
          <div className={`p-4 rounded-lg mb-6 ${
            messageType === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : messageType === 'error'
              ? 'bg-red-50 border border-red-200 text-red-700'
              : 'bg-blue-50 border border-blue-200 text-blue-700'
          }`}>
            <div className="flex items-center">
              <CheckCircleIcon className={`w-5 h-5 mr-2 ${
                messageType === 'success' ? 'text-green-500' : 
                messageType === 'error' ? 'text-red-500' : 'text-blue-500'
              }`} />
              {message}
            </div>
          </div>
        )}

        {/* Company selector -- every field below is scoped to whichever
            company is selected here. This didn't exist before: the form had
            no company concept, so it always edited a single global row. */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company *
          </label>
          <select
            value={selectedCompanyId}
            onChange={handleCompanyChange}
            disabled={loadingCompanies}
            required
            className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none shadow-sm bg-white ${
              loadingCompanies ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <option value="">Select a company</option>
            {companies.map((company) => {
              const hasSetup = allSetups.some(s => String(s.companyId) === String(company.id) && s.isActive);
              return (
                <option key={company.id} value={company.id}>
                  {company.name} {hasSetup ? '(configured)' : '(not configured)'}
                </option>
              );
            })}
          </select>
          {loadingCompanies && (
            <p className="text-gray-500 text-sm mt-1">Loading companies...</p>
          )}
        </div>

        {/* Form Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {[
            { 
              label: 'SMTP Server *', 
              name: 'smtpServer', 
              type: 'text', 
              placeholder: 'smtp.gmail.com',
              icon: <CogIcon className="w-4 h-4 text-gray-400" />
            },
            { 
              label: 'SMTP Port *', 
              name: 'smtpPort', 
              type: 'number', 
              placeholder: '587',
              icon: <span className="text-gray-400 text-sm">#</span>
            },
            { 
              label: 'Username *', 
              name: 'username', 
              type: 'text', 
              placeholder: 'your-email@gmail.com',
              icon: <span className="text-gray-400 text-sm">@</span>
            },
            { 
              label: 'Password *', 
              name: 'password', 
              type: 'password', 
              placeholder: existingSetup ? 'Leave blank to keep current password' : 'Your email password or app password',
              icon: <span className="text-gray-400 text-sm">•••</span>
            },
            { 
              label: 'From Email *', 
              name: 'fromEmail', 
              type: 'email', 
              placeholder: 'noreply@yourcompany.com',
              icon: <EnvelopeIcon className="w-4 h-4 text-gray-400" />
            },
            { 
              label: 'From Name *', 
              name: 'fromName', 
              type: 'text', 
              placeholder: 'Your Company Name',
              icon: <span className="text-gray-400 text-sm">👤</span>
            },
          ].map((field) => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {field.label}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {field.icon}
                </div>
                <input
                  type={field.type}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  required={field.name !== 'password' || !existingSetup}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none shadow-sm text-gray-800 transition-colors"
                />
              </div>
              {field.name === 'password' && existingSetup && (
                <p className="text-xs text-gray-500 mt-1">Leave blank to keep current password</p>
              )}
            </div>
          ))}
        </div>

        {/* Security Settings */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Security Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                name="enableSsl"
                checked={formData.enableSsl}
                onChange={handleChange}
                className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-400"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Enable SSL</span>
                <p className="text-xs text-gray-500 mt-1">Use SSL encryption for secure connection</p>
              </div>
            </label>
            
            <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                name="useTls"
                checked={formData.useTls}
                onChange={handleChange}
                className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-400"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Use TLS</span>
                <p className="text-xs text-gray-500 mt-1">Use TLS for enhanced security</p>
              </div>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || loading}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
          >
            {testing ? (
              <>
                <span className="animate-spin border-2 border-gray-400 border-t-transparent rounded-full w-4 h-4"></span>
                Testing Connection...
              </>
            ) : (
              <>
                <span>🔧</span>
                Test Connection
              </>
            )}
          </button>
          
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"></span>
                Saving...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                {existingSetup ? 'Update Setup' : 'Save Setup'}
              </>
            )}
          </button>
        </div>

        {/* Current Setup Info */}
        {existingSetup && (
          <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Current Setup</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-500">Server</div>
                <div className="font-semibold text-gray-800">{existingSetup.smtpServer}:{existingSetup.smtpPort}</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-500">From Address</div>
                <div className="font-semibold text-gray-800">{existingSetup.fromName}</div>
                <div className="text-xs text-gray-500">{existingSetup.fromEmail}</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-500">Status</div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                  existingSetup.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {existingSetup.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        )}
      </motion.form>
    </div>
  );
};

export default EmailSetup;